export interface BookingTimeItem {
  id: string;
  startAt: string;
  endAt: string;
}

export type BookingListSort = 'start-asc' | 'start-desc';

/** Sorterar på starttid. Närmast först är stigande. */
export function sortBookings<T extends BookingTimeItem>(items: T[], sort: BookingListSort): T[] {
  const direction = sort === 'start-desc' ? -1 : 1;
  return [...items].sort(
    (a, b) => direction * (new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
  );
}

/** Bokningar som överlappar det lokala dygnet. */
export function bookingsOnLocalDay<T extends BookingTimeItem>(items: T[], now: Date): T[] {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const end = start + 24 * 60 * 60 * 1000;
  return items.filter((item) => new Date(item.startAt).getTime() < end && new Date(item.endAt).getTime() >= start);
}

/** Kommande och pågående bokningar, tidigast start först. Avslutade tas bort. */
export function upcomingBookings<T extends BookingTimeItem>(items: T[], now: Date): T[] {
  const nowMs = now.getTime();
  return items
    .filter((item) => new Date(item.endAt).getTime() >= nowMs)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
}

export interface MonthBookingBar {
  key: string;
  label: string;
  completed: number;
  upcoming: number;
}

const MONTHS_IN_CHART = 6;
const FUTURE_MONTHS_SHOWN = 1;

function monthIndex(date: Date, timeZone: string): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric' }).formatToParts(date);
  return {
    year: Number(parts.find((part) => part.type === 'year')?.value),
    month: Number(parts.find((part) => part.type === 'month')?.value),
  };
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Sex månader: fem bakåt från en månad framåt, så nästa månads bokningar syns.
 * Avslutade och kommande staplas var för sig. Samma bokning räknas en gång.
 */
export function monthlyBookingBars(
  items: Array<BookingTimeItem & { status?: string }>,
  now: Date,
  locale: string,
  timeZone: string
): MonthBookingBar[] {
  const current = monthIndex(now, timeZone);
  const end = shiftMonth(current.year, current.month, FUTURE_MONTHS_SHOWN);
  const start = shiftMonth(end.year, end.month, -(MONTHS_IN_CHART - 1));
  const bars: MonthBookingBar[] = [];

  for (let i = 0; i < MONTHS_IN_CHART; i++) {
    const point = shiftMonth(start.year, start.month, i);
    const labelDate = new Date(Date.UTC(point.year, point.month - 1, 15));
    bars.push({
      key: monthKey(point.year, point.month),
      label: labelDate.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }).replace('.', ''),
      completed: 0,
      upcoming: 0,
    });
  }

  const byKey = new Map(bars.map((bar) => [bar.key, bar]));
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    const when = monthIndex(new Date(item.startAt), timeZone);
    const bar = byKey.get(monthKey(when.year, when.month));
    if (!bar) continue;
    const completed = item.status === 'RETURNED' || new Date(item.endAt).getTime() < now.getTime();
    if (completed) bar.completed += 1;
    else bar.upcoming += 1;
  }

  return bars;
}

export interface BookingKindItem extends BookingTimeItem {
  bookingKind?: string | null;
  fuelType?: string | null;
}

/** Ren el, inte hybrid. */
export function isElectricFuel(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const value = fuelType.trim().toLowerCase();
  if (value.includes('hybrid')) return false;
  if (value === 'electric' || value === 'ev' || value === 'bev' || value === 'battery') return true;
  if (value.startsWith('el')) return true;
  return value.includes('electric') || value.includes('elektr');
}

/** Andel el av hyrda bilar, avrundat till hela procent. */
export function electricSharePercent(electricCars: number, cars: number): number {
  if (cars <= 0) return 0;
  return Math.round((electricCars / cars) * 100);
}

/** Bokningar vars start ligger i de senaste sex månaderna, inklusive innevarande. */
export function sixMonthBookingMix(
  items: BookingKindItem[],
  now: Date,
  timeZone: string
): { total: number; cars: number; resources: number; electricCars: number } {
  const current = monthIndex(now, timeZone);
  const start = shiftMonth(current.year, current.month, -5);
  const keys = new Set<string>();
  for (let i = 0; i < 6; i++) {
    const point = shiftMonth(start.year, start.month, i);
    keys.add(monthKey(point.year, point.month));
  }

  const seen = new Set<string>();
  let cars = 0;
  let resources = 0;
  let electricCars = 0;
  let total = 0;
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    const when = monthIndex(new Date(item.startAt), timeZone);
    if (!keys.has(monthKey(when.year, when.month))) continue;
    total += 1;
    if (item.bookingKind === 'resource') resources += 1;
    else if (item.bookingKind === 'car') {
      cars += 1;
      if (isElectricFuel(item.fuelType)) electricCars += 1;
    }
  }
  return { total, cars, resources, electricCars };
}

/** Visar körda km när resor finns, annars bokningens uppskattade sträcka. */
export function periodKilometers(drivenKm: number, estimatedKm: number): number {
  return drivenKm > 0 ? drivenKm : estimatedKm;
}

export function formatSek(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount);
}

export interface BookingDayBadge {
  today: boolean;
  day: string;
  month: string;
  weekday: string;
}

/** Datumruta: IDAG när starten är samma lokala dygn, annars veckodag. */
export function bookingDayBadge(iso: string, now: Date, locale: string, timeZone: string): BookingDayBadge {
  const date = new Date(iso);
  const dayKey = (value: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(value);
  const formatted = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date).replace('.', '');
  return {
    today: dayKey(date) === dayKey(now),
    day: formatted({ day: 'numeric' }),
    month: formatted({ month: 'short' }).toLowerCase(),
    weekday: formatted({ weekday: 'short' }),
  };
}

/** Tid kvar till start. started är sant när bokningen redan har börjat. */
export function leadTimeUntil(startAt: string, now: Date): { hours: number; minutes: number; started: boolean } {
  const diff = new Date(startAt).getTime() - now.getTime();
  if (diff <= 0) return { hours: 0, minutes: 0, started: true };
  const totalMinutes = Math.floor(diff / 60000);
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    started: false,
  };
}
